import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, HStack, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import BusinessTypesDropdown from '../../../../../../components/BusinessTypesDropdown';
import FormInput from '../../../../../../components/form/FormInput';
import CompanySettingTypes from '../../../../../../constants/companySettings';
import { getValidationObject } from '../../../../../../constants/regexPatterns';
import { selectCompanySettingValue } from '../../../../../../redux/modules/company';
import { useInsuredData } from '../../../../../../services/insured-service';
import UseFormMethodsType from '../../../../../../types/UseFormMethodsType';
import InsuredInfoPanel from '../InsuredInfoPanel';

const InsuredInfo = ({ onClose, insuredId, useFormMethods, onSubmit }) => {
  const { data: insuredData, isLoading: insuredLoading } = useInsuredData(insuredId);

  const insuredType = useMemo(() => {
    if (insuredLoading) {
      return null;
    }

    return insuredData?.insuredType;
  }, [insuredData, insuredLoading]);

  const disableExternalId = useSelector(selectCompanySettingValue(CompanySettingTypes.ReportsHideNonPolicyHolders));

  const {
    handleSubmit,
    setValue
  } = useFormMethods;

  const topLeftFormStack = [
    {
      name: 'insured_company_name',
      placeholder: 'Insured Company Name (DBA)',
      required: true,
      validation: {
        required: {
          value: true,
          message: 'Insured name is a required field'
        },
        minLength: {
          value: 2,
          message: 'Insured name must have at least two characters'
        },
        maxLength: {
          value: 200,
          message: 'Insured name must have fewer than 200 characters'
        },
        ...getValidationObject('alphaNumericAndCharacters')
      }
    },
    {
      name: 'external_id',
      placeholder: 'Insured External ID',
      validation: getValidationObject('alphaNumericAndCharacters'),
      disabled: disableExternalId
    }
  ];

  const bottomLeftFormStack = [
    {
      name: 'guarantor_name',
      placeholder: 'Guarantor Name (If Applicable)',
      validation: {
        maxLength: {
          value: 191,
          message: 'Guarantor name must have fewer than 191 characters'
        },
        ...getValidationObject('alphaNumericAndCharacters')
      }
    },
    {
      name: 'legal_name',
      placeholder: 'Legal Name (If Applicable)',
      validation: {
        maxLength: {
          value: 191,
          message: 'Legal name must have fewer than 191 characters'
        },
        ...getValidationObject('alphaNumericAndCharacters')
      }
    }
  ];

  const rightTopFormStack = [
    {
      name: 'insured_phone',
      label: 'Primary Phone'
    },
    {
      name: 'insured_phone2',
      label: 'Secondary Phone'
    }
  ];

  const rightBottomFormStack = [
    {
      name: 'insured_description',
      placeholder: 'Insured Description',
      type: 'textArea',
      validation: {
        maxLength: {
          value: 255,
          message: 'Insured description must be fewer than 255 characters'
        }
      }
    }
  ];

  return (
    <InsuredInfoPanel
      isDisabled={insuredData?.deletedAt}
      title="insured info"
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
    >
      <form>
        <HStack alignItems="flex-start" justifyContent="space-between" pb={4} spacing={6} zIndex={4}>
          <VStack
            alignItems="flex-start"
            flexBasis="50%"
            spacing={0}
          >
            {topLeftFormStack.map(input => (
              <FormInput
                inputName={input.name}
                isDisabled={input.disabled}
                key={input.name}
                labelName={input.placeholder}
                placeholder={input.placeholder}
                required={input?.required}
                useFormMethods={useFormMethods}
                validationRules={input?.validation}
              />
            ))}
            {bottomLeftFormStack.map(input => (
              <FormInput
                inputName={input.name}
                isDisabled={input.disabled}
                key={input.name}
                labelName={input.placeholder}
                placeholder={input.placeholder}
                required={input?.required}
                useFormMethods={useFormMethods}
                validationRules={input?.validation}
              />
            ))}
          </VStack>
          <VStack
            alignItems="flex-start"
            flexBasis="50%"
            maxW="50%"
            spacing={0}
          >
            <HStack spacing={4}>
              {rightTopFormStack.map(input => (
                <FormInput
                  inputName={input.name}
                  key={input.name}
                  labelName={input.label}
                  placeholder="(555) 555-5555"
                  type="phoneNumber"
                  useFormMethods={useFormMethods}
                  validationRules={getValidationObject('phoneNumber')}
                />
              ))}
            </HStack>

            <Box width="100%">
              <BusinessTypesDropdown defaultValue={insuredType} onChange={value => setValue('insured_type', value)} />
            </Box>

            {rightBottomFormStack.map(input => (
              <FormInput
                inputName={input.name}
                key={input.name}
                labelName={input.placeholder}
                placeholder={input.placeholder}
                type={input?.type}
                useFormMethods={useFormMethods}
                validationRules={input?.validation}
              />
            ))}
          </VStack>
        </HStack>
      </form>
    </InsuredInfoPanel>
  );
};

InsuredInfo.propTypes = {
  onClose: PropTypes.func.isRequired,
  insuredId: PropTypes.number.isRequired,
  useFormMethods: UseFormMethodsType
};

export default InsuredInfo;
